import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { NodeKind, StrategyAsset, StrategyNode } from '@/constants/strategy-builder'

import { useEffect, useMemo, useState } from 'react'

import {
    BriefcaseBusiness,
    CalendarDays,
    CirclePlay,
    Clapperboard,
    FileText,
    FlaskConical,
    Flag,
    GitBranch,
    Image as ImageIcon,
    Info,
    ListChecks,
    Maximize,
    Megaphone,
    MessageSquare,
    Paperclip,
    Play,
    Rocket,
    Save,
    SlidersHorizontal,
    Waypoints,
    Workflow,
    Zap,
    ZoomIn,
    ZoomOut
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { StatusLabel } from '@/components/generals/status-label'

import {
    guidedSteps,
    strategyAssets,
    strategyBuilder,
    strategyEdges,
    strategyNodes,
    strategyPaths
} from '@/constants/strategy-builder'

type Mode = 'visual' | 'guided'
type PanelTab = 'configure' | 'test' | 'demo'
type PathId = typeof strategyPaths[number]['id']

const { width: W, height: H, nodeWidth: NW, nodeHeight: NH } = strategyBuilder.canvas

const kindStyles: Record<NodeKind, { icon: LucideIcon, tag: string, accent: string, chip: string }> = {
    start: { icon: CirclePlay, tag: 'Start', accent: 'border-soft-white/35', chip: 'text-soft-white/80' },
    conversation: { icon: MessageSquare, tag: 'Conversation', accent: 'border-sky-400/50', chip: 'text-sky-300' },
    decision: { icon: GitBranch, tag: 'Decision', accent: 'border-violet-400/60', chip: 'text-violet-300' },
    action: { icon: Zap, tag: 'Action', accent: 'border-orange/60', chip: 'text-orange' },
    end: { icon: Flag, tag: 'End', accent: 'border-soft-white/35', chip: 'text-soft-white/80' }
}

const assetIcons: Record<StrategyAsset['icon'], LucideIcon> = {
    image: ImageIcon,
    info: Info,
    pdf: FileText,
    promo: Megaphone,
    event: CalendarDays,
    service: BriefcaseBusiness
}

const nodeById = (id: string): StrategyNode => strategyNodes.find(node => node.id === id) ?? strategyNodes[0]

const ConceptControl: FC<{ icon: LucideIcon, children: ReactNode }> = ({ icon: Icon, children }): ReactNode => (
    <span
        title='Illustration only'
        className='inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-dashed border-soft-white/20 px-3 text-xs font-medium text-cool-grey-light'
    >
        <Icon className='size-3.5' aria-hidden />
        {children}
    </span>
)

const StrategyBuilderSection: FC = (): ReactNode => {
    const [mode, setMode] = useState<Mode>('visual')
    const [selectedId, setSelectedId] = useState('checkpoint')
    const [panelTab, setPanelTab] = useState<PanelTab>('configure')
    const [pathId, setPathId] = useState<PathId>('interested')
    const [step, setStep] = useState(-1)
    const [playing, setPlaying] = useState(false)
    const [selectedAsset, setSelectedAsset] = useState('asset-01')

    const path = strategyPaths.find(item => item.id === pathId) ?? strategyPaths[0]
    const selected = nodeById(selectedId)
    const SelectedKindIcon = kindStyles[selected.kind].icon

    const litNodes = useMemo(() => new Set<string>(step >= 0 ? path.nodes.slice(0, step + 1) : []), [path, step])

    const litEdges = useMemo(() => {
        const set = new Set<string>()
        for (let index = 1; index <= step; index++) set.add(`${path.nodes[index - 1]}>${path.nodes[index]}`)
        return set
    }, [path, step])

    useEffect(() => {
        if (window.matchMedia('(max-width: 767px)').matches) setMode('guided')
    }, [])

    useEffect(() => {
        if (!playing) return

        if (step >= path.nodes.length - 1) {
            setPlaying(false)
            return
        }

        const timer = window.setTimeout(() => {
            setStep(current => current + 1)
            setSelectedId(path.nodes[Math.min(step + 1, path.nodes.length - 1)])
        }, 900)

        return () => window.clearTimeout(timer)
    }, [playing, step, path])

    const playWalkthrough = (): void => {
        setPanelTab('demo')

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setStep(path.nodes.length - 1)
            setSelectedId(path.nodes[path.nodes.length - 1])
            return
        }

        setStep(0)
        setSelectedId(path.nodes[0])
        setPlaying(true)
    }

    const choosePath = (id: PathId): void => {
        setPlaying(false)
        setStep(-1)
        setPathId(id)
    }

    const selectNode = (id: string): void => {
        setSelectedId(id)
        if (panelTab === 'demo') setPanelTab('configure')
    }

    const linkedAsset = strategyAssets.find(asset => asset.id === selected.asset)
    const assetDetail = strategyAssets.find(asset => asset.id === selectedAsset) ?? strategyAssets[0]

    return (
        <section
            id='pitch-strategy-builder'
            aria-labelledby='pitch-strategy-builder-heading'
            className='relative overflow-hidden border-t border-soft-white/10 bg-navy-900 py-24 sm:py-32'
        >
            <div aria-hidden className='absolute -top-40 left-1/2 h-80 w-[60rem] -translate-x-1/2 rounded-full bg-violet-500/10 blur-[120px]' />

            <div className='container-site relative'>
                <SectionHeading
                    id='pitch-strategy-builder-heading'
                    eyebrow='Long-term product capability'
                    title={strategyBuilder.title}
                    intro={strategyBuilder.statement}
                    aside={<StatusLabel status='futureVision' />}
                />

                <p className='reveal mt-5 max-w-3xl leading-relaxed text-cool-grey-light'>
                    {strategyBuilder.relation}
                </p>

                <p className='mt-6 inline-flex items-center gap-2 rounded-lg border border-dashed border-soft-white/25 px-3 py-2 text-sm font-semibold text-soft-white'>
                    {strategyBuilder.disclaimer}
                </p>

                <div className='reveal mt-10 overflow-hidden rounded-2xl border border-soft-white/10 bg-[#0A1521] shadow-[0_50px_120px_-50px_rgb(0_0_0/0.9)]'>
                    <div className='flex flex-wrap items-center gap-3 border-b border-soft-white/10 bg-navy-950/80 px-4 py-3'>
                        <div className='flex items-center gap-2'>
                            <Workflow className='size-4 text-orange' aria-hidden />
                            <span className='text-sm font-semibold text-soft-white'>
                                Pitch Strategy Builder
                            </span>
                            <span className='hidden rounded-md bg-navy-800 px-2 py-0.5 text-xs text-cool-grey-light sm:inline'>
                                Draft: {strategyBuilder.strategyName}
                            </span>
                        </div>

                        <div role='group' aria-label='Builder mode' className='flex rounded-lg bg-navy-800 p-1 lg:mx-auto'>
                            {([
                                { id: 'guided', label: 'Guided', icon: ListChecks },
                                { id: 'visual', label: 'Visual', icon: Waypoints }
                            ] as { id: Mode, label: string, icon: LucideIcon }[]).map(item => (
                                <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={mode === item.id}
                                    onClick={() => setMode(item.id)}
                                    className={cn(
                                        'inline-flex min-h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition',
                                        mode === item.id ? 'bg-orange text-navy' : 'text-cool-grey-light hover:text-soft-white'
                                    )}
                                >
                                    <item.icon className='size-3.5' aria-hidden />
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        <div className='flex w-full flex-wrap items-center gap-2 lg:w-auto'>
                            <ConceptControl icon={Save}>
                                Save Draft
                            </ConceptControl>

                            <ConceptControl icon={Rocket}>
                                Activate Strategy
                            </ConceptControl>

                            <button
                                type='button'
                                aria-pressed={panelTab === 'test'}
                                onClick={() => setPanelTab('test')}
                                className='inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-navy-800 px-3 text-xs font-semibold text-soft-white ring-1 ring-soft-white/10 transition hover:bg-navy-700'
                            >
                                <FlaskConical className='size-3.5 text-orange' aria-hidden />
                                Test This Node
                            </button>

                            <button
                                type='button'
                                aria-pressed={panelTab === 'demo'}
                                onClick={() => setPanelTab('demo')}
                                className='inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-navy-800 px-3 text-xs font-semibold text-soft-white ring-1 ring-soft-white/10 transition hover:bg-navy-700'
                            >
                                <Clapperboard className='size-3.5 text-orange' aria-hidden />
                                Demo Studio
                            </button>
                        </div>
                    </div>

                    <div className='grid xl:grid-cols-[1fr_330px]'>
                        <div className='min-w-0 border-b border-soft-white/10 xl:border-r xl:border-b-0'>
                            {mode === 'visual' ? (
                                <>
                                    <div className='hidden overflow-x-auto md:block'>
                                        <div
                                            className='relative min-w-[880px] bg-[radial-gradient(circle_at_center,rgb(244_247_250/0.08)_1px,transparent_1.5px)] bg-size-[22px_22px]'
                                            style={{ aspectRatio: `${W} / ${H}` }}
                                        >
                                            <svg viewBox={`0 0 ${W} ${H}`} className='absolute inset-0 size-full' aria-hidden>
                                                <defs>
                                                    <marker id='arrow-dim' viewBox='0 0 10 10' refX='9' refY='5' markerWidth='7' markerHeight='7' orient='auto-start-reverse'>
                                                        <path d='M0 0 L10 5 L0 10 z' fill='rgb(244 247 250 / 0.35)' />
                                                    </marker>
                                                    <marker id='arrow-lit' viewBox='0 0 10 10' refX='9' refY='5' markerWidth='7' markerHeight='7' orient='auto-start-reverse'>
                                                        <path d='M0 0 L10 5 L0 10 z' fill='#FF8A3D' />
                                                    </marker>
                                                </defs>

                                                {strategyEdges.map(edge => {
                                                    const from = nodeById(edge.from)
                                                    const to = nodeById(edge.to)
                                                    const sx = from.x + NW / 2
                                                    const tx = to.x - NW / 2 - 4
                                                    const dx = (tx - sx) / 2
                                                    const lit = litEdges.has(`${edge.from}>${edge.to}`)

                                                    return (
                                                        <path
                                                            key={`${edge.from}>${edge.to}`}
                                                            d={`M ${sx} ${from.y} C ${sx + dx} ${from.y}, ${tx - dx} ${to.y}, ${tx} ${to.y}`}
                                                            fill='none'
                                                            stroke={lit ? '#FF8A3D' : 'rgb(244 247 250 / 0.22)'}
                                                            strokeWidth={lit ? 2.5 : 1.5}
                                                            markerEnd={lit ? 'url(#arrow-lit)' : 'url(#arrow-dim)'}
                                                            className='transition-[stroke] duration-300'
                                                        />
                                                    )
                                                })}
                                            </svg>

                                            {strategyEdges.filter(edge => edge.label).map(edge => {
                                                const from = nodeById(edge.from)
                                                const to = nodeById(edge.to)
                                                const mx = (from.x + NW / 2 + to.x - NW / 2) / 2
                                                const my = (from.y + to.y) / 2

                                                return (
                                                    <span
                                                        key={edge.label}
                                                        aria-hidden
                                                        className='absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-violet-200 ring-1 ring-violet-400/30'
                                                        style={{ left: `${(mx / W) * 100}%`, top: `${(my / H) * 100}%` }}
                                                    >
                                                        {edge.label}
                                                    </span>
                                                )
                                            })}

                                            {strategyNodes.map(node => {
                                                const style = kindStyles[node.kind]
                                                const Icon = style.icon
                                                const isSelected = node.id === selectedId
                                                const isLit = litNodes.has(node.id)

                                                return (
                                                    <button
                                                        key={node.id}
                                                        type='button'
                                                        aria-pressed={isSelected}
                                                        aria-label={`${node.label}, ${style.tag} node`}
                                                        onClick={() => selectNode(node.id)}
                                                        className={cn(
                                                            'absolute flex flex-col justify-center gap-1 rounded-lg border bg-navy-900 px-2.5 text-left shadow-[0_10px_24px_-14px_rgb(0_0_0/0.9)] transition duration-300 hover:-translate-y-0.5',
                                                            style.accent,
                                                            isLit && 'bg-orange/15',
                                                            isSelected && 'ring-2 ring-orange ring-offset-2 ring-offset-[#0A1521]'
                                                        )}
                                                        style={{
                                                            left: `${((node.x - NW / 2) / W) * 100}%`,
                                                            top: `${((node.y - NH / 2) / H) * 100}%`,
                                                            width: `${(NW / W) * 100}%`,
                                                            height: `${(NH / H) * 100}%`
                                                        }}
                                                    >
                                                        <span className={cn('flex items-center gap-1 text-[9px] font-semibold tracking-wider uppercase', style.chip)}>
                                                            <Icon className='size-3' aria-hidden />
                                                            {style.tag}
                                                        </span>

                                                        <span className='text-[11px] leading-tight font-semibold text-soft-white'>
                                                            {node.label}
                                                        </span>

                                                        {node.asset && (
                                                            <span className='absolute -top-2.5 -right-2 inline-flex items-center gap-0.5 rounded-full bg-orange px-1.5 py-0.5 text-[9px] font-bold text-navy'>
                                                                <Paperclip className='size-2.5' aria-hidden />
                                                                Asset 01
                                                            </span>
                                                        )}
                                                    </button>
                                                )
                                            })}

                                            <div aria-hidden className='absolute bottom-3 left-3 flex items-center gap-1 rounded-lg bg-navy-900/90 p-1 ring-1 ring-soft-white/10'>
                                                <span className='grid size-7 place-items-center text-cool-grey-light'><ZoomIn className='size-3.5' /></span>
                                                <span className='grid size-7 place-items-center text-cool-grey-light'><ZoomOut className='size-3.5' /></span>
                                                <span className='grid size-7 place-items-center text-cool-grey-light'><Maximize className='size-3.5' /></span>
                                                <span className='px-1.5 text-[10px] text-cool-grey-light'>100%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='flex flex-col gap-3 p-4 md:hidden'>
                                        <p className='text-xs text-cool-grey-light'>
                                            Read-only overview of the sample workflow. The full canvas is a desktop view.
                                        </p>

                                        {strategyPaths.map(item => (
                                            <div key={item.id} className='rounded-xl border border-soft-white/10 bg-navy-900/70 p-3'>
                                                <p className='text-[11px] font-semibold tracking-wider text-violet-200 uppercase'>
                                                    {item.label}
                                                </p>

                                                <p className='mt-1.5 text-sm leading-relaxed text-soft-white/90'>
                                                    {item.nodes.slice(2).map(id => nodeById(id).label).join(' → ')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <ol className='grid gap-3 p-4 sm:grid-cols-2 sm:p-5'>
                                    {guidedSteps.map((guided, index) => {
                                        const node = nodeById(guided.nodeId)
                                        const isSelected = node.id === selectedId
                                        const isLit = litNodes.has(node.id)

                                        return (
                                            <li key={guided.nodeId}>
                                                <button
                                                    type='button'
                                                    aria-pressed={isSelected}
                                                    onClick={() => selectNode(node.id)}
                                                    className={cn(
                                                        'flex h-full w-full flex-col gap-2 rounded-xl border bg-navy-900/80 p-4 text-left transition',
                                                        isSelected ? 'border-orange/70 ring-1 ring-orange/40' : 'border-soft-white/10 hover:border-soft-white/25',
                                                        isLit && 'bg-orange/10'
                                                    )}
                                                >
                                                    <span className='flex items-center gap-2'>
                                                        <span className='grid size-6 place-items-center rounded-full bg-navy-700 text-[11px] font-bold text-soft-white'>
                                                            {index + 1}
                                                        </span>

                                                        <span className='font-heading text-sm font-bold text-soft-white'>
                                                            {guided.title}
                                                        </span>
                                                    </span>

                                                    <span className='text-xs leading-relaxed text-cool-grey-light'>
                                                        {node.purpose}
                                                    </span>

                                                    <span className='mt-auto flex flex-wrap items-center gap-1.5 pt-1 text-[10px]'>
                                                        <span className='rounded-full bg-navy-800 px-2 py-0.5 text-cool-grey-light'>
                                                            Node: {node.label}
                                                        </span>

                                                        <span className='rounded-full bg-navy-800 px-2 py-0.5 text-cool-grey-light'>
                                                            Tone: {node.tonePreset}
                                                        </span>
                                                    </span>
                                                </button>
                                            </li>
                                        )
                                    })}
                                </ol>
                            )}
                        </div>

                        <aside aria-label='Node panel' className='flex flex-col'>
                            <div role='tablist' aria-label='Panel views' className='flex border-b border-soft-white/10'>
                                {([
                                    { id: 'configure', label: 'Configure', icon: SlidersHorizontal },
                                    { id: 'test', label: 'Test This Node', icon: FlaskConical },
                                    { id: 'demo', label: 'Demo Studio', icon: Clapperboard }
                                ] as { id: PanelTab, label: string, icon: LucideIcon }[]).map(tab => (
                                    <button
                                        key={tab.id}
                                        type='button'
                                        role='tab'
                                        aria-selected={panelTab === tab.id}
                                        onClick={() => setPanelTab(tab.id)}
                                        className={cn(
                                            'flex min-h-11 flex-1 items-center justify-center gap-1.5 border-b-2 px-2 text-[11px] font-semibold transition',
                                            panelTab === tab.id ? 'border-orange text-soft-white' : 'border-transparent text-cool-grey-light hover:text-soft-white'
                                        )}
                                    >
                                        <tab.icon className='size-3.5' aria-hidden />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div role='tabpanel' className='flex-1 p-5 text-sm'>
                                {panelTab !== 'demo' && (
                                    <div className='flex items-center gap-2'>
                                        <span className={cn('grid size-8 place-items-center rounded-lg bg-navy-800', kindStyles[selected.kind].chip)}>
                                            <SelectedKindIcon className='size-4' aria-hidden />
                                        </span>

                                        <div className='leading-tight'>
                                            <p className='font-semibold text-soft-white'>
                                                {selected.label}
                                            </p>

                                            <p className='text-[11px] text-cool-grey-light'>
                                                {kindStyles[selected.kind].tag} node
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {panelTab === 'configure' && (
                                    <dl className='mt-5 flex flex-col gap-4'>
                                        <div>
                                            <dt className='text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>Node purpose</dt>
                                            <dd className='mt-1 text-soft-white/90'>{selected.purpose}</dd>
                                        </div>

                                        <div>
                                            <dt className='text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>Behaviour instructions</dt>
                                            <dd className='mt-1 text-soft-white/90'>{selected.behaviour}</dd>
                                        </div>

                                        <div>
                                            <dt className='text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>Expected customer responses</dt>
                                            <dd className='mt-1.5 flex flex-wrap gap-1.5'>
                                                {selected.responses.map(response => (
                                                    <span key={response} className='rounded-full bg-navy-800 px-2 py-0.5 text-xs text-soft-white/85'>
                                                        {response}
                                                    </span>
                                                ))}
                                            </dd>
                                        </div>

                                        <div className='grid grid-cols-2 gap-3'>
                                            <div>
                                                <dt className='text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>Connected asset</dt>
                                                <dd className='mt-1 text-soft-white/90'>{linkedAsset ? `Asset ${linkedAsset.number}, ${linkedAsset.title}` : 'None'}</dd>
                                            </div>

                                            <div>
                                                <dt className='text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>Next destination</dt>
                                                <dd className='mt-1 text-soft-white/90'>{selected.next}</dd>
                                            </div>
                                        </div>

                                        <div>
                                            <dt className='flex items-center justify-between text-[11px] font-semibold tracking-wider text-cool-grey-light uppercase'>
                                                Tone
                                                <span className='normal-case tracking-normal text-orange'>{selected.tonePreset}</span>
                                            </dt>

                                            <dd className='mt-2 flex flex-col gap-2'>
                                                {Object.entries(selected.tone).map(([dimension, value]) => (
                                                    <div key={dimension} className='grid grid-cols-[6.5rem_1fr_2rem] items-center gap-2 text-xs'>
                                                        <span className='text-cool-grey-light'>{dimension}</span>
                                                        <span className='h-1.5 overflow-hidden rounded-full bg-navy-800'>
                                                            <span className='block h-full rounded-full bg-orange transition-[width] duration-500' style={{ width: `${value}%` }} />
                                                        </span>
                                                        <span className='text-right text-soft-white/80'>{value}</span>
                                                    </div>
                                                ))}
                                            </dd>
                                        </div>
                                    </dl>
                                )}

                                {panelTab === 'test' && (
                                    <div className='mt-5 flex flex-col gap-3'>
                                        <p className='text-xs text-cool-grey-light'>
                                            Isolated test of this one checkpoint, outside the full strategy.
                                        </p>

                                        <div className='rounded-xl bg-navy-800/80 p-3'>
                                            <p className='text-[11px] font-semibold text-cool-grey-light'>Sample visitor</p>
                                            <p className='mt-1 text-soft-white/90'>{selected.test.visitor}</p>
                                        </div>

                                        <div className='rounded-xl bg-navy-700 p-3'>
                                            <p className='text-[11px] font-semibold text-orange'>Sample Bucky response</p>
                                            <p className='mt-1 text-soft-white'>{selected.test.bucky}</p>
                                        </div>

                                        <p className='text-xs text-cool-grey-light'>
                                            Outcome: <span className='font-semibold text-violet-200'>{selected.test.outcome}</span>
                                        </p>

                                        <p className='mt-1 rounded-lg border border-dashed border-soft-white/20 px-3 py-2 text-xs text-cool-grey-light'>
                                            Sample only. Nothing is tested, saved or executed.
                                        </p>
                                    </div>
                                )}

                                {panelTab === 'demo' && (
                                    <div className='flex flex-col gap-4'>
                                        <p className='text-xs text-cool-grey-light'>
                                            Walk a sample customer through the whole strategy.
                                        </p>

                                        <div role='group' aria-label='Sample customer path' className='flex flex-wrap gap-1.5'>
                                            {strategyPaths.map(item => (
                                                <button
                                                    key={item.id}
                                                    type='button'
                                                    aria-pressed={pathId === item.id}
                                                    onClick={() => choosePath(item.id)}
                                                    className={cn(
                                                        'min-h-9 rounded-full border px-3 text-xs font-semibold transition',
                                                        pathId === item.id ? 'border-violet-400/70 bg-violet-500/15 text-violet-100' : 'border-soft-white/15 text-cool-grey-light hover:text-soft-white'
                                                    )}
                                                >
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>

                                        <button
                                            type='button'
                                            onClick={playWalkthrough}
                                            disabled={playing}
                                            className='inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange px-4 text-sm font-bold text-navy transition hover:bg-orange-light disabled:opacity-60'
                                        >
                                            <Play className='size-4' aria-hidden />
                                            {playing ? 'Walking through…' : 'Play sample walkthrough'}
                                        </button>

                                        <ol className='flex flex-col gap-1.5' aria-live='polite'>
                                            {path.nodes.map((id, index) => (
                                                <li
                                                    key={id}
                                                    className={cn(
                                                        'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs transition',
                                                        index <= step ? 'bg-orange/15 text-soft-white' : 'text-cool-grey-light'
                                                    )}
                                                >
                                                    <span className={cn('size-1.5 rounded-full', index <= step ? 'bg-orange' : 'bg-soft-white/25')} aria-hidden />
                                                    {nodeById(id).label}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>

                    <div className='border-t border-soft-white/10 bg-navy-950/60 p-4 sm:p-5'>
                        <p className='text-[11px] font-semibold tracking-[0.14em] text-cool-grey-light uppercase'>
                            Visual asset cards
                        </p>

                        <ul className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6'>
                            {strategyAssets.map(asset => {
                                const Icon = assetIcons[asset.icon]
                                const linked = asset.id === 'asset-01'
                                const glowing = linked && (selectedId === 'show-asset' || litNodes.has('show-asset'))

                                return (
                                    <li key={asset.id}>
                                        <button
                                            type='button'
                                            aria-pressed={selectedAsset === asset.id}
                                            onClick={() => setSelectedAsset(asset.id)}
                                            className={cn(
                                                'relative flex w-full flex-col gap-2 rounded-xl border bg-navy-900 p-3 text-left transition',
                                                glowing ? 'border-orange shadow-[0_0_0_3px_rgb(255_138_61/0.25)]' : selectedAsset === asset.id ? 'border-soft-white/35' : 'border-soft-white/10 hover:border-soft-white/25'
                                            )}
                                        >
                                            <span className='grid aspect-[4/3] w-full place-items-center rounded-lg bg-[linear-gradient(135deg,#1B3350,#0D1C2D)] text-orange'>
                                                <Icon className='size-6' aria-hidden />
                                            </span>

                                            <span className='text-[10px] font-semibold text-cool-grey-light'>
                                                Asset {asset.number} · {asset.type}
                                            </span>

                                            <span className='text-xs leading-tight font-semibold text-soft-white'>
                                                {asset.title}
                                            </span>

                                            {linked && (
                                                <span className='inline-flex items-center gap-1 text-[10px] font-semibold text-orange'>
                                                    <Paperclip className='size-3' aria-hidden />
                                                    Show relevant asset
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>

                        <p className='mt-3 text-xs text-cool-grey-light' aria-live='polite'>
                            Asset {assetDetail.number}, {assetDetail.title} ({assetDetail.type}). {assetDetail.id === 'asset-01' ? 'Connected to the Show relevant asset node.' : 'Not connected in this sample strategy.'}
                        </p>
                    </div>
                </div>

                <p className='mt-5 text-sm text-cool-grey-light'>
                    {strategyBuilder.sameStrategy}
                </p>
            </div>
        </section>
    )
}

export default StrategyBuilderSection
